import { ActivityReviewService } from 'src/api/activity/activity-review/activity-review.service';
import { ProgramProvider } from 'src/providers/program.provider';
import { Component, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, startWith, take, takeUntil, switchMap } from 'rxjs/operators';
import { routerTransition } from 'src/animations';
import { DoenKidsGenericApiProvider } from 'src/providers/generic.provider';
import { PermissionProvider } from 'src/providers/permission.provider';
import { ScreenSizeProvider } from 'src/providers/screen-size.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { NewsItemService } from 'src/api/customer/news-item/news-item.service';
import { NewsfeedProvider } from 'src/providers/newsfeed-provider';
import { OrganizationUnitTypeNamePipe } from 'src/pipes/organization-unit-type-name';
import { TranslateService } from 'src/app/utils/translate.service';
import { ActivityToReviewCountProvider } from 'src/providers/activity-to-review-count.provider';
import { IOrganizationUnitOverview } from 'typings/doenkids/doenkids';
import { SupportProvider } from 'src/providers/support.provider';
import { environment } from 'src/environments/environment';
import { ThemeService } from 'src/app/core/services/theme.service';
import { version } from 'packageJson';

interface ISideNavItem {
  isEnabled$: Observable<boolean>;
  name: string;
  nameParams?: { [key: string]: string };
  icon: string;
  routerLink?: string;
  url?: string;
  badgeValue$?: Observable<number | string>;
}

export const openMenu: Subject<void> = new Subject<void>();

const tagName = 'app-split-pane';

@Component({
  selector: tagName,
  templateUrl: './split-pane.component.html',
  styleUrls: ['./split-pane.component.scss'],
  animations: [routerTransition],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SplitPaneComponent implements OnInit, OnDestroy {
  public isDrawerCollapsed = signal(false);

  @ViewChild(MatSidenav) drawer: MatSidenav;

  isXSmall$: Observable<boolean>;

  sidenavMode$: Observable<'over' | 'side'>;

  sidenavOpened$: Observable<boolean>;

  isAdmin$: Observable<boolean>;

  hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$: Observable<boolean>;

  newsItemCount$: BehaviorSubject<number>;

  reviewNewCount$ = new BehaviorSubject(0);

  currentOUDetails$: Observable<IOrganizationUnitOverview>;

  isOrganizationOfTypeCustomer$: Observable<boolean>;

  isOrganizationOfTypeGroup$: Observable<boolean>;

  isOrganizationOfTypeLocation$: Observable<boolean>;

  isRootOrganizationUnit$: Observable<boolean>;

  buildVersion?: string;

  stop$ = new Subject();

  routes$: Observable<ISideNavItem[]>;

  translationsHaveLoaded$ = new BehaviorSubject<boolean>(false);

  public logoUrl$: Observable<string>;

  public logoComponent$: Observable<any>;

  public logoTitle$: Observable<string>;

  public hasCustomLogoComponent$: Observable<boolean>;

  public poweredByLogo$: Observable<string>;

  public showPoweredByLogo$: Observable<boolean>;

  public showLogo = environment.showLogoAboveNavigation;

  constructor(
    private $activityToReviewCountService: ActivityToReviewCountProvider,
    private $newsItem: NewsItemService,
    private $permission: PermissionProvider,
    private $review: ActivityReviewService,
    private $screenSize: ScreenSizeProvider,
    private $session: DoenkidsSessionProvider,
    private $support: SupportProvider,
    private $translateService: TranslateService,
    private genericAPI: DoenKidsGenericApiProvider,
    private newsfeedProvider: NewsfeedProvider,
    private organizationUnitTypeNamePipe: OrganizationUnitTypeNamePipe,
    private programProvider: ProgramProvider,
    private themeService: ThemeService,
  ) {
    // Load theme-specific assets reactively
    this.logoUrl$ = this.themeService.currentTheme$.pipe(
      map(theme => theme.assets.logo),
      takeUntil(this.stop$),
    );
    this.logoComponent$ = this.themeService.currentTheme$.pipe(
      map(theme => theme.assets.logoComponent),
      takeUntil(this.stop$),
    );
    this.logoTitle$ = this.themeService.currentTheme$.pipe(
      map(theme => theme.assets.logoTitle || ''),
      takeUntil(this.stop$),
    );
    this.hasCustomLogoComponent$ = this.themeService.currentTheme$.pipe(
      map(theme => !!theme.assets.logoComponent),
      takeUntil(this.stop$),
    );
    this.poweredByLogo$ = this.themeService.currentTheme$.pipe(
      map(theme => theme.assets.poweredByLogo),
      takeUntil(this.stop$),
    );
    this.showPoweredByLogo$ = this.themeService.currentTheme$.pipe(
      map(theme => !!theme.assets.poweredByLogo && theme.assets.poweredByLogo !== ''),
      takeUntil(this.stop$),
    );
    this.isXSmall$ = this.$screenSize?.isXSmall$;

    // Create reactive streams for sidenav properties
    this.sidenavMode$ = this.isXSmall$.pipe(
      map((isXSmall) => (isXSmall ? 'over' : 'side') as 'over' | 'side'),
      takeUntil(this.stop$),
    );

    this.sidenavOpened$ = this.isXSmall$.pipe(
      map((isXSmall) => !isXSmall),
      takeUntil(this.stop$),
    );
    this.isAdmin$ = this.$session.isAdmin$;

    this.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$ = this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$.pipe(
      takeUntil(this.stop$),
    );

    this.currentOUDetails$ = this.$session.getOrganizationUnit$.pipe(takeUntil(this.stop$));

    this.isOrganizationOfTypeCustomer$ = this.$session.isCurrentOrganizationUnitIsOfTypeCustomer$.pipe(takeUntil(this.stop$));

    this.isOrganizationOfTypeGroup$ = this.$session.isCurrentOrganizationUnitIsOfTypeGroup$.pipe(takeUntil(this.stop$));

    this.isOrganizationOfTypeLocation$ = this.$session.isCurrentOrganizationUnitIsOfTypeLocation$.pipe(takeUntil(this.stop$));

    this.isRootOrganizationUnit$ = this.$session.isRootOrganization$.pipe(takeUntil(this.stop$));

    this.newsItemCount$ = new BehaviorSubject(0);

    // Helper to get theme icon with fallback - defined in constructor scope to maintain 'this' context
    const getIcon = (iconKey: string, fallback: string): string => {
      const icons = this.themeService.currentTheme?.icons;
      return icons?.[iconKey] || fallback;
    };

    this.routes$ = combineLatest([
      this.currentOUDetails$,
      this.$translateService.onInitialTranslationAndLangOrTranslationChange$,
      this.themeService.currentTheme$,
    ]).pipe(
      map(([organization]) => {
        const organizationUnitDisplayValue = this.$translateService.instant(
          this.organizationUnitTypeNamePipe.transform(organization.organization_unit_type_id),
        );

        const navigation: ISideNavItem[] = [
          {
            isEnabled$: of(true),
            icon: getIcon('activities', 'filter_none'),
            name: _('split_pane.navigation.activities'),
            routerLink: 'activity-overview',
            badgeValue$: combineLatest([
              this.isAdmin$,
              this.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$,
              this.$activityToReviewCountService.activityNewCount$,
              this.currentOUDetails$,
            ]).pipe(
              map(([isAdmin, hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree, activityNewCount]) => {
                const hasPermissions = isAdmin === true || hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree === true;
                const count = activityNewCount || 0;

                if (hasPermissions && count > 0) {
                  return count;
                }
                return null;
              }),
            ),
          },
          {
            isEnabled$: this.isRootOrganizationUnit$,
            icon: getIcon('baseActivities', 'filter_none'),
            name: _('split_pane.navigation.base_activities'),
            routerLink: 'base-activities',
          },
          {
            isEnabled$: of(true),
            icon: getIcon('examples', 'library_books'),
            name: _('split_pane.navigation.examples'),
            badgeValue$: combineLatest([this.isAdmin$, this.currentOUDetails$, this.$activityToReviewCountService.programTemplateNewCount$]).pipe(
              map(([isAdmin, , programTemplateNewCount]) => {
                const hasPermissions = isAdmin === true;
                const count = programTemplateNewCount || 0;

                if (hasPermissions && count > 0) {
                  return count;
                }
                return null;
              }),
            ),
            routerLink: `/organization/${organization.id}/templates`,
          },
          {
            isEnabled$: this.isRootOrganizationUnit$,
            icon: getIcon('baseExamples', 'library_books'),
            name: _('split_pane.navigation.base_examples'),
            routerLink: `/organization/${organization.id}/base/templates`,
          },
          {
            isEnabled$: of(true),
            icon: getIcon('calendar', 'calendar_today'),
            name: _('split_pane.navigation.calendar'),
            badgeValue$: combineLatest([
              this.$session.ouCountryCode$,
              this.$permission.canSeeUnpublishedProgramCountPermission$,
              this.programProvider?.unpublishedProgramCount$,
              this.currentOUDetails$,
            ]).pipe(
              map(([ouCountryCode, canSeeUnpublishedProgramCount, unpublishedPrograms]) => {
                const count = ouCountryCode?.toLowerCase() === 'nl' && canSeeUnpublishedProgramCount ? unpublishedPrograms.count : 0;
                return count === 0 ? null : count;
              }),
            ),
            routerLink: 'calendar',
          },
          {
            isEnabled$: this.isOrganizationOfTypeLocation$,
            icon: getIcon('pinboard', 'push_pin'),
            name: _('split_pane.navigation.pinboard'),
            routerLink: `/organization/${organization.id}/pinbord`,
          },
          {
            isEnabled$: combineLatest([
              this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$,
              this.$permission.hasOUWritePermissions$,
            ]).pipe(
              map(
                ([hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree, hasWritePermission]) =>
                  hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree && hasWritePermission,
              ),
            ),
            icon: getIcon('organizationUsers', 'account_box'),
            name: _('split_pane.navigation.organization_unit_users'),
            nameParams: { organizationUnitDisplayValue },
            routerLink: `/organization/${organization.id}/users`,
          },
          {
            isEnabled$: combineLatest([this.$session.isReader$, this.isOrganizationOfTypeGroup$, this.$permission.hasOUWritePermissions$]).pipe(
              map(([isReader, isGroup, hasWritePermission]) => !isGroup && (isReader || hasWritePermission)),
            ),
            icon: getIcon('organizationDetails', 'domain'),
            name: _('split_pane.navigation.organization_unit_overview'),
            nameParams: { organizationUnitDisplayValue },
            routerLink: `/organization/${organization.id}/overview`,
          },
          {
            isEnabled$: of(true),
            icon: getIcon('settings', 'settings'),
            name: _('split_pane.navigation.settings'),
            routerLink: 'settings',
          },
          {
            isEnabled$: combineLatest([this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$, this.$session.isReader$]).pipe(
              map(
                ([hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree, isReader]) =>
                  hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree || isReader,
              ),
            ),
            icon: getIcon('activeUsers', 'admin_panel_settings'),
            name: _('split_pane.navigation.active_users'),
            routerLink: 'active-users',
          },
          {
            isEnabled$: combineLatest([
              this.isOrganizationOfTypeCustomer$,
              this.$permission.hasAnalyticsPermission$,
              this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$,
            ]).pipe(map(([isCustomer, hasAnalyticsPermission, hasWritePermission]) => isCustomer && hasAnalyticsPermission && hasWritePermission)),
            icon: getIcon('insights', 'insights'),
            name: _('split_pane.navigation.insights'),
            routerLink: `/organization/${organization.id}/insights`,
          },
          {
            isEnabled$: of(true),
            icon: getIcon('newsItems', 'article'),
            name: _('split_pane.navigation.news_items'),
            badgeValue$: combineLatest([this.currentOUDetails$, this.newsfeedProvider.itemMarkedAsRead$.pipe(startWith(null))]).pipe(
              switchMap(() => {
                this.fetchNewsItemCount();
                return this.newsItemCount$.pipe(map((count) => (count > 0 ? count : null)));
              }),
            ),
            routerLink: '/',
          },
          {
            isEnabled$: combineLatest([
              this.isRootOrganizationUnit$,
              this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$,
              this.$session.isReader$,
            ]).pipe(map(([isRootOrganization, hasWritePermission, isReader]) => isRootOrganization && (isReader || hasWritePermission))),
            icon: getIcon('baseNewsItems', 'article'),
            name: _('split_pane.navigation.base_news_items'),
            routerLink: 'base-news-items',
          },
          {
            isEnabled$: combineLatest([this.isOrganizationOfTypeCustomer$, this.$permission.manageReviewsPermission$]).pipe(
              map(([isCustomer, hasReviewPermission]) => isCustomer && hasReviewPermission),
            ),
            icon: getIcon('reviews', 'reviews'),
            name: _('split_pane.navigation.reviews'),
            badgeValue$: combineLatest([this.currentOUDetails$, this.$review.reviewUpdated$.pipe(startWith(''))]).pipe(
              switchMap(() => {
                this.fetchReviewNewCount();
                return this.reviewNewCount$.pipe(map((count) => (count > 0 ? count : null)));
              }),
            ),
            routerLink: 'review-approval',
          },
          {
            isEnabled$: this.$support.showSupportItems$,
            icon: 'help',
            name: _('split_pane.navigation.help'),
            url: this.$support.supportUrl$.value,
          },
        ];

        return navigation;
      }),
    );

    openMenu.pipe(takeUntil(this.stop$)).subscribe(() => {
      this.drawer.toggle();
    });

    this.$translateService.onInitialTranslationAndLangOrTranslationChange$.pipe(take(1)).subscribe(() => {
      this.translationsHaveLoaded$.next(true);
    });
  }

  async ngOnInit() {
    this.buildVersion = version;

    combineLatest([this.currentOUDetails$, this.newsfeedProvider.itemMarkedAsRead$])
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        this.fetchNewsItemCount();
      });

    combineLatest([this.currentOUDetails$, this.$review.reviewUpdated$.pipe(startWith(''))])
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        this.fetchReviewNewCount();
      });

    combineLatest([this.currentOUDetails$, this.$permission.canSeeUnpublishedProgramCountPermission$])
      .pipe(takeUntil(this.stop$), debounceTime(300))
      .subscribe(([ou, permission]) => {
        if (permission) {
          this.programProvider.fetchUnpublishedProgramCount(ou.id);
        } else {
          this.programProvider.setUnpublishedCount(0);
        }
      });
  }

  ngOnDestroy(): void {
    this.stop$.next(undefined);
  }

  async fetchNewsItemCount() {
    const currentOuId = (await firstValueFrom(this.currentOUDetails$))?.id;
    const result = await this.$newsItem.fetchUnreadTotal(currentOuId);

    // Always update the count, even if result is null/undefined
    this.newsItemCount$.next(result?.count ?? 0);
  }

  async fetchReviewNewCount() {
    const currentOuId = (await firstValueFrom(this.currentOUDetails$))?.id;
    const result = await this.$review.fetchNewCount(currentOuId);

    // Always update the count, even if result is null/undefined
    this.reviewNewCount$.next(result?.count ?? 0);
  }

  toggleDrawerCollapseState() {
    this.isDrawerCollapsed.set(!this.isDrawerCollapsed());
  }
}
